"use client"

import { useEffect, useRef, useCallback } from "react"

export function CyberGrid() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isVisibleRef = useRef(true)
  const rafIdRef = useRef<number>(0)
  const lastTimeRef = useRef(0)

  const draw = useCallback((timestamp: number) => {
    if (!isVisibleRef.current) {
      rafIdRef.current = requestAnimationFrame(draw)
      return
    }

    // Throttle to ~30fps for performance
    if (timestamp - lastTimeRef.current < 33) {
      rafIdRef.current = requestAnimationFrame(draw)
      return
    }
    lastTimeRef.current = timestamp

    const canvas = canvasRef.current
    if (!canvas) {
      rafIdRef.current = requestAnimationFrame(draw)
      return
    }

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      rafIdRef.current = requestAnimationFrame(draw)
      return
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const gridSize = 40
    const horizonY = canvas.height * 0.4
    const speed = 0.5
    const time = (timestamp * speed * 0.05) % gridSize

    // Batch stroke operations
    ctx.lineWidth = 1
    
    // Vertical lines (perspective)
    for (let i = -canvas.width; i < canvas.width * 2; i += gridSize) {
      const x = i + time
      const endX = (x - canvas.width / 2) * 4 + canvas.width / 2

      const gradient = ctx.createLinearGradient(x, horizonY, x, canvas.height)
      gradient.addColorStop(0, "rgba(184, 41, 255, 0)")
      gradient.addColorStop(0.3, "rgba(184, 41, 255, 0.15)")
      gradient.addColorStop(1, "rgba(0, 163, 255, 0.25)")

      ctx.beginPath()
      ctx.moveTo(x, horizonY)
      ctx.lineTo(endX, canvas.height)
      ctx.strokeStyle = gradient
      ctx.stroke()
    }

    // Horizontal lines (moving down)
    ctx.lineWidth = 0.5
    for (let i = 0; i < 30; i++) {
      const y = horizonY + Math.pow(i / 30, 2) * (canvas.height - horizonY)
      const nextY = horizonY + Math.pow((i + 1) / 30, 2) * (canvas.height - horizonY)
      const offset = (timestamp * speed * 0.025) % (nextY - y)
      const drawY = y + offset

      if (drawY > canvas.height) continue

      const alpha = 0.1 + (i / 30) * 0.15
      ctx.beginPath()
      ctx.moveTo(0, drawY)
      ctx.lineTo(canvas.width, drawY)
      ctx.strokeStyle = `rgba(0, 240, 255, ${alpha})`
      ctx.stroke()
    }

    // Horizon glow - simplified
    const horizonGradient = ctx.createLinearGradient(0, horizonY - 50, 0, horizonY + 50)
    horizonGradient.addColorStop(0, "rgba(184, 41, 255, 0)")
    horizonGradient.addColorStop(0.5, "rgba(184, 41, 255, 0.3)")
    horizonGradient.addColorStop(1, "rgba(0, 163, 255, 0)")
    ctx.fillStyle = horizonGradient
    ctx.fillRect(0, horizonY - 50, canvas.width, 100)

    rafIdRef.current = requestAnimationFrame(draw)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 1.5)
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      const ctx = canvas.getContext("2d")
      if (ctx) {
        ctx.scale(dpr, dpr)
      }
    }

    resize()

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting
      },
      { threshold: 0 }
    )
    observer.observe(canvas)

    window.addEventListener("resize", resize)
    
    const timeoutId = setTimeout(() => {
      rafIdRef.current = requestAnimationFrame(draw)
    }, 250)

    return () => {
      window.removeEventListener("resize", resize)
      observer.disconnect()
      clearTimeout(timeoutId)
      cancelAnimationFrame(rafIdRef.current)
    }
  }, [draw])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-60"
      style={{ 
        mixBlendMode: "screen",
        willChange: "contents",
      }}
      aria-hidden="true"
    />
  )
}
